import { useEffect, useState } from 'react';
import { Group, Paper, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { useNavigate } from 'react-router';
import logger from '@ayocore/platform/common/helpers/logger';
import type { ProjectWelcomeStat } from '../../../server/functions/getProjectWelcomeStats';
import { getProjectWelcomeStats } from '../../rpc';
import { formatDdMm, weekNo } from '../kanban/WeekSelectionContext';

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.28)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: 'none',
} as const;

/** Строка-ссылка на вкладку дашборда проекта: подпись слева, счётчик справа. */
function StatRow({
  label,
  value,
  onClick,
}: {
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      px={8}
      py={4}
      style={{ borderRadius: 8, backgroundColor: '#ffffff5e' }}
    >
      <Group justify='space-between' gap='xs' wrap='nowrap'>
        <Text size='xs' c='dimmed'>
          {label}
        </Text>
        <Text size='sm' fw={600}>
          {value}
        </Text>
      </Group>
    </UnstyledButton>
  );
}

/**
 * Карточки проектов на главной: текущая неделя + счётчики-ссылки на вкладки
 * дашборда проекта (?tab=requests|inspections|ncr). Заявки/инспекции — за
 * текущую отчётную неделю, NCR — накопительно по проекту.
 */
export function ProjectWelcomeCards() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ProjectWelcomeStat[]>([]);

  useEffect(() => {
    let cancelled = false;
    getProjectWelcomeStats()
      .then(rows => {
        if (!cancelled) setStats(rows);
      })
      .catch(error => {
        logger.error('[ProjectWelcomeCards] Не удалось загрузить статистику проектов:', error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (stats.length === 0) return null;

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing='md'>
      {stats.map(stat => {
        const base = `/mws/projects/${stat.project_id}`;
        return (
          <Paper key={stat.project_id} p='md' radius='lg' style={glassStyle}>
            <Stack gap='xs'>
              <UnstyledButton onClick={() => navigate(base)}>
                <Text size='sm' fw={600}>
                  {stat.project_no} · {stat.name}
                </Text>
                <Text size='xs' c='dimmed'>
                  {stat.week
                    ? `${formatDdMm(stat.week.date_from)}–${formatDdMm(stat.week.date_to)} · неделя ${weekNo(stat.week)}`
                    : 'Нет текущей недели'}
                </Text>
              </UnstyledButton>
              <Stack gap={4}>
                <StatRow
                  label='Заявки'
                  value={stat.week ? String(stat.requests_count) : '—'}
                  onClick={() => navigate(`${base}?tab=requests`)}
                />
                <StatRow
                  label='Инспекции'
                  value={
                    stat.week ? `${stat.inspections_done} / ${stat.inspections_total}` : '—'
                  }
                  onClick={() => navigate(`${base}?tab=inspections`)}
                />
                <StatRow
                  label='NCR открыто'
                  value={`${stat.ncr_open} / ${stat.ncr_total}`}
                  onClick={() => navigate(`${base}?tab=ncr`)}
                />
                <StatRow
                  label='Отчёты'
                  value={`${stat.reports_final} / ${stat.reports_total}`}
                  onClick={() => navigate(`${base}?tab=reports`)}
                />
                <StatRow
                  label='Файлы'
                  value={String(stat.files_count)}
                  onClick={() => navigate(`${base}?tab=files`)}
                />
              </Stack>
            </Stack>
          </Paper>
        );
      })}
    </SimpleGrid>
  );
}
